import React from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useResultPanel, type PanelTab } from './resultPanelStore';

export const TAB_LABEL: Record<PanelTab, string> = { evidence: 'Evidence', data: 'Data', sql: 'SQL', trace: 'Trace', context: 'Context' };

const TAB_ICON: Record<PanelTab, React.ReactNode> = {
  evidence: <FactCheckOutlinedIcon sx={{ fontSize: 15 }} />,
  data: <TableChartOutlinedIcon sx={{ fontSize: 15 }} />,
  sql: <CodeOutlinedIcon sx={{ fontSize: 15 }} />,
  trace: <TimelineOutlinedIcon sx={{ fontSize: 15 }} />,
  context: <InfoOutlinedIcon sx={{ fontSize: 15 }} />,
};

export interface PanelTabSpec {
  key: PanelTab;
  /** what sits above the tab's body: copy, download, Explore, the full trace link */
  toolbar?: React.ReactNode;
  body: () => React.ReactNode;
}

/** The answer row's quiet buttons: borderless, a word and its icon. */
export const ROW_ACT = {
  height: 30, px: 1.125, gap: 0.75, borderRadius: '7px', fontSize: 13, color: 'var(--ink-3)',
  '&:hover': { bgcolor: 'var(--hover, var(--surface2))', color: 'var(--ink)' },
  '&[aria-expanded="true"]': { bgcolor: 'var(--active, var(--hover, var(--surface2)))', color: 'var(--ink)' },
} as const;

/**
 * Where an answer's panel opens: the thread's side panel when the view provides one (`ThreadView`), else in place below
 * the answer's row. Context follows a portal, so the panel knows which it is in.
 */
export const SidePanelSlot = React.createContext<HTMLElement | null>(null);

/**
 * The answer's quiet row: a word and icon per panel tab, each opening the panel, then `primary` (Explore); the open
 * tab's word closes it again.
 */
export function ResultCard({ owner, tabs, primary }: {
  owner: string;
  tabs: PanelTab[];
  primary?: React.ReactNode;
}) {
  const open = useResultPanel((s) => s.open);
  const openPanel = useResultPanel((s) => s.openPanel);
  const closePanel = useResultPanel((s) => s.closePanel);
  if (!tabs.length) return null;
  const on = open?.owner === owner ? open.tab : null;
  return (
    <Box data-testid="result-card" data-open={on ? 'true' : 'false'} data-no-copy
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap', minHeight: 30 }}>
      {tabs.map((t) => (
        <ButtonBase key={t} onClick={() => (on === t ? closePanel(owner) : openPanel(owner, t))} aria-expanded={on === t}
          data-testid={`result-card-tab-${t}`} sx={ROW_ACT}>
          {TAB_ICON[t]}{TAB_LABEL[t]}
        </ButtonBase>
      ))}
      {primary}
    </Box>
  );
}

/**
 * What a word in the row opens (#1933): underline tabs and a close button, then the tab's toolbar and its body — in
 * the thread's side panel (full screen on a phone), or in place below the row where the view has none. It is never
 * part of the answer's own text (the action row's Copy reads the answer's DOM). Escape, ✕ or the word again close it;
 * it closes with the answer that owns it.
 */
export function ResultPanel({ owner, subtitle, tabs }: {
  owner: string;
  subtitle?: string;
  tabs: PanelTabSpec[];
}) {
  const open = useResultPanel((s) => s.open);
  const openPanel = useResultPanel((s) => s.openPanel);
  const closePanel = useResultPanel((s) => s.closePanel);
  const side = React.useContext(SidePanelSlot) !== null;
  const mine = open?.owner === owner;
  React.useEffect(() => () => closePanel(owner), [owner, closePanel]);
  React.useEffect(() => {
    if (!mine) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(owner); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mine, owner, closePanel]);
  if (!mine || !tabs.length) return null;
  const active = tabs.find((t) => t.key === open!.tab) || tabs[0];
  return (
    <Box role="region" aria-label="How this answer was worked out" data-testid="result-panel" data-tab={active.key} data-side={side ? 'true' : 'false'}
      sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, bgcolor: 'var(--panel, var(--surface))',
        ...(side ? { flex: 1 } : { border: '1px solid var(--line)', borderRadius: '12px', overflow: 'hidden' }) }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, px: 2, pt: side ? 1.5 : 0.5,
        borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
        <Box role="tablist" aria-label="Details" sx={{ display: 'flex', gap: 2.25, overflowX: 'auto', minWidth: 0 }}>
          {tabs.map((t) => (
            <ButtonBase key={t.key} role="tab" aria-selected={t.key === active.key} data-testid={`result-panel-tab-${t.key}`}
              onClick={() => openPanel(owner, t.key)}
              sx={{ py: 1.25, fontSize: 13.5, whiteSpace: 'nowrap', color: t.key === active.key ? 'var(--ink)' : 'var(--ink-3)',
                fontWeight: t.key === active.key ? 500 : 400, borderRadius: 0,
                boxShadow: t.key === active.key ? 'inset 0 -2px 0 var(--ink)' : 'inset 0 -2px 0 transparent',
                '&:hover': { color: 'var(--ink)' } }}>
              {TAB_LABEL[t.key]}
            </ButtonBase>
          ))}
        </Box>
        <IconButton aria-label="Close panel" size="small" onClick={() => closePanel(owner)}
          sx={{ mb: 0.75, color: 'var(--ink-3)', borderRadius: '7px', '&:hover': { bgcolor: 'var(--hover, var(--surface2))', color: 'var(--ink)' } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflow: 'auto', flex: side ? 1 : undefined }}>
        {(subtitle || active.toolbar) && (
          <Box data-testid="result-panel-toolbar" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 28, fontSize: 12.5, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
            {subtitle && <Box component="span" sx={{ mr: 'auto', minWidth: 0 }}>{subtitle}</Box>}
            {active.toolbar}
          </Box>
        )}
        <Box data-testid="result-panel-body" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {active.body()}
        </Box>
      </Box>
    </Box>
  );
}

/** Where the answer's row and panel land under the reply. Rendered alone, they stay where they are. */
export interface ReplySlots { row: HTMLElement | null; panel: HTMLElement | null }
export function InSlot({ at, slotted, children }: { at: HTMLElement | null; slotted: boolean; children: React.ReactNode }) {
  if (!slotted) return <>{children}</>;
  return at ? createPortal(children, at) : null;
}

/**
 * The thread's side panel (`ThreadView`): the open answer's panel lands here, on the right, and full screen on a phone.
 * Empty, it takes no room.
 */
export const SidePanelHost = React.forwardRef<HTMLDivElement>(function SidePanelHost(_props, ref) {
  return (
    <Box ref={ref} data-testid="answer-side-panel"
      sx={{ flex: '0 1 460px', minWidth: 320, maxWidth: '50%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', minHeight: 0,
        borderLeft: '1px solid var(--line)', bgcolor: 'var(--panel, var(--surface))', boxShadow: '-12px 0 32px -24px rgba(16,16,24,.25)',
        '&:empty': { display: 'none' },
        '@media (max-width: 767px)': { position: 'fixed', inset: 0, zIndex: 1300, minWidth: 0, maxWidth: 'none', borderLeft: 0, boxShadow: 'none' } }} />
  );
});
